import { CipherCalculation, CipherBreakdown } from './types';

// Hebrew Character Tables
const HEBREW_MAP: Record<string, { standard: number; gadol: number }> = {
  'א': { standard: 1, gadol: 1 },
  'ב': { standard: 2, gadol: 2 },
  'ג': { standard: 3, gadol: 3 },
  'ד': { standard: 4, gadol: 4 },
  'ה': { standard: 5, gadol: 5 },
  'ו': { standard: 6, gadol: 6 },
  'ז': { standard: 7, gadol: 7 },
  'ח': { standard: 8, gadol: 8 },
  'ט': { standard: 9, gadol: 9 },
  'י': { standard: 10, gadol: 10 },
  'כ': { standard: 20, gadol: 20 },
  'ך': { standard: 20, gadol: 500 },
  'ל': { standard: 30, gadol: 30 },
  'מ': { standard: 40, gadol: 40 },
  'ם': { standard: 40, gadol: 600 },
  'נ': { standard: 50, gadol: 50 },
  'ן': { standard: 50, gadol: 700 },
  'ס': { standard: 60, gadol: 60 },
  'ע': { standard: 70, gadol: 70 },
  'פ': { standard: 80, gadol: 80 },
  'ף': { standard: 80, gadol: 800 },
  'צ': { standard: 90, gadol: 90 },
  'ץ': { standard: 90, gadol: 900 },
  'ק': { standard: 100, gadol: 100 },
  'ר': { standard: 200, gadol: 200 },
  'ש': { standard: 300, gadol: 300 },
  'ת': { standard: 400, gadol: 400 }
};

const LATIN_TO_HEBREW_MAP: Record<string, number> = {
  'A': 1, 'B': 2, 'C': 20, 'D': 4, 'E': 5, 'F': 80, 'G': 3, 'H': 8,
  'I': 10, 'J': 10, 'K': 20, 'L': 30, 'M': 40, 'N': 50, 'O': 70, 'P': 80,
  'Q': 100, 'R': 200, 'S': 60, 'T': 400, 'U': 6, 'V': 6, 'W': 6, 'X': 60,
  'Y': 10, 'Z': 7
};

// Greek Character Tables
const GREEK_MAP: Record<string, number> = {
  'Α': 1, 'α': 1, 'Β': 2, 'β': 2, 'Γ': 3, 'γ': 3, 'Δ': 4, 'δ': 4,
  'Ε': 5, 'ε': 5, 'Ϝ': 6, 'ϛ': 6, 'Ζ': 7, 'ζ': 7, 'Η': 8, 'η': 8,
  'Θ': 9, 'θ': 9, 'Ι': 10, 'ι': 10, 'Κ': 20, 'κ': 20, 'Λ': 30, 'λ': 30,
  'Μ': 40, 'μ': 40, 'Ν': 50, 'ν': 50, 'Ξ': 60, 'ξ': 60, 'Ο': 70, 'ο': 70,
  'Π': 80, 'π': 80, 'Ρ': 100, 'ρ': 100, 'Σ': 200, 'σ': 200, 'ς': 200,
  'Τ': 300, 'τ': 300, 'Υ': 400, 'υ': 400, 'Φ': 500, 'φ': 500, 'Χ': 600, 'χ': 600,
  'Ψ': 700, 'ψ': 700, 'Ω': 800, 'ω': 800, 'ϡ': 900
};

const LATIN_TO_GREEK_MAP: Record<string, number> = {
  'A': 1, 'B': 2, 'C': 20, 'D': 4, 'E': 5, 'F': 500, 'G': 3, 'H': 8,
  'I': 10, 'J': 10, 'K': 20, 'L': 30, 'M': 40, 'N': 50, 'O': 70, 'P': 80,
  'Q': 90, 'R': 100, 'S': 200, 'T': 300, 'U': 400, 'V': 400, 'W': 800, 'X': 60,
  'Y': 400, 'Z': 7
};

// Agrippa Latin Qabalah
const AGRIPPA_MAP: Record<string, number> = {
  'A': 1, 'B': 2, 'C': 3, 'D': 4, 'E': 5, 'F': 6, 'G': 7, 'H': 8, 'I': 9,
  'K': 10, 'L': 20, 'M': 30, 'N': 40, 'O': 50, 'P': 60, 'Q': 70, 'R': 80, 'S': 90,
  'T': 100, 'U': 200, 'X': 300, 'Y': 400, 'Z': 500, 'J': 600, 'V': 700, 'W': 900
};

// English Qabalah (ALW Key 11)
const ENGLISH_ALW_MAP: Record<string, number> = {
  'A': 1, 'B': 20, 'C': 13, 'D': 6, 'E': 25, 'F': 18, 'G': 11, 'H': 4, 'I': 23,
  'J': 16, 'K': 9, 'L': 2, 'M': 21, 'N': 14, 'O': 7, 'P': 26, 'Q': 19, 'R': 12,
  'S': 5, 'T': 24, 'U': 17, 'V': 10, 'W': 3, 'X': 22, 'Y': 15, 'Z': 8
};

// Dukante Enneadic Gates & Demons
const DUKANTE_GATES: Record<number, { demon: string; element: string }> = {
  1: { demon: 'Satan', element: 'Primal Void / All' },
  2: { demon: 'Belial', element: 'Earth / Bedrock' },
  3: { demon: 'Lucifer', element: 'Air / Illumination' },
  4: { demon: 'Flereous', element: 'Fire / Will' },
  5: { demon: 'Leviathan', element: 'Water / Chaos' },
  6: { demon: 'Eurynomous', element: 'Death / Necromancy' },
  7: { demon: 'Verrine', element: 'Health / Healing' },
  8: { demon: 'Amducious', element: 'War / Destruction' },
  9: { demon: 'Azazel / Luithian', element: 'Transmutation' }
};

const DUKANTE_MAP: Record<string, number> = {
  'A': 1, 'J': 10, 'S': 19,
  'B': 2, 'K': 11, 'T': 20,
  'C': 3, 'L': 12, 'U': 21,
  'D': 4, 'M': 13, 'V': 22,
  'E': 5, 'N': 14, 'W': 23,
  'F': 6, 'O': 15, 'X': 24,
  'G': 7, 'P': 16, 'Y': 25,
  'H': 8, 'Q': 17, 'Z': 26,
  'I': 9, 'R': 18
};

export function theosophicReduction(num: number): number {
  if (num <= 0) return 0;
  let current = Math.abs(num);
  while (current > 9) {
    current = current.toString().split('').reduce((acc, digit) => acc + parseInt(digit, 10), 0);
  }
  return current;
}

export function calculateCiphers(input: string, satanicShiftMode: boolean = false): CipherCalculation {
  const sanitized = input.trim();
  const upper = sanitized.toUpperCase();

  // 1. Hebrew
  let hebrewStandard = 0;
  let hebrewGadol = 0;
  const hebrewBreakdown: CipherBreakdown[] = [];

  for (const ch of sanitized) {
    if (HEBREW_MAP[ch]) {
      const { standard, gadol } = HEBREW_MAP[ch];
      hebrewStandard += standard;
      hebrewGadol += gadol;
      hebrewBreakdown.push({ char: ch, val: standard });
    } else {
      const up = ch.toUpperCase();
      if (LATIN_TO_HEBREW_MAP[up] !== undefined) {
        const val = LATIN_TO_HEBREW_MAP[up];
        hebrewStandard += val;
        hebrewGadol += val;
        hebrewBreakdown.push({ char: ch, val });
      }
    }
  }

  // 2. Greek
  let greekSum = 0;
  const greekBreakdown: CipherBreakdown[] = [];

  for (const ch of sanitized) {
    if (GREEK_MAP[ch] !== undefined) {
      const val = GREEK_MAP[ch];
      greekSum += val;
      greekBreakdown.push({ char: ch, val });
    } else {
      const up = ch.toUpperCase();
      if (LATIN_TO_GREEK_MAP[up] !== undefined) {
        const val = LATIN_TO_GREEK_MAP[up];
        greekSum += val;
        greekBreakdown.push({ char: ch, val });
      }
    }
  }

  // 3. Agrippa
  let agrippaSum = 0;
  const agrippaBreakdown: CipherBreakdown[] = [];

  for (const ch of upper) {
    if (AGRIPPA_MAP[ch] !== undefined) {
      const val = AGRIPPA_MAP[ch];
      agrippaSum += val;
      agrippaBreakdown.push({ char: ch, val });
    }
  }

  // 4. English ALW
  let englishSum = 0;
  const englishBreakdown: CipherBreakdown[] = [];

  for (const ch of upper) {
    if (ENGLISH_ALW_MAP[ch] !== undefined) {
      const val = ENGLISH_ALW_MAP[ch];
      englishSum += val;
      englishBreakdown.push({ char: ch, val });
    }
  }

  // 5. Dukante / Satanic
  let dukanteSum = 0;
  const dukanteBreakdown: CipherBreakdown[] = [];

  for (const ch of upper) {
    if (satanicShiftMode) {
      const code = ch.charCodeAt(0);
      if (code >= 65 && code <= 90) {
        const val = (code - 64) + 35; // A=36 ... Z=61
        dukanteSum += val;
        dukanteBreakdown.push({ char: ch, val });
      }
    } else {
      if (DUKANTE_MAP[ch] !== undefined) {
        const val = DUKANTE_MAP[ch];
        dukanteSum += val;
        dukanteBreakdown.push({ char: ch, val });
      }
    }
  }

  const gate = ((dukanteSum - 1) % 9 + 9) % 9 + 1;
  const primaryDemon = DUKANTE_GATES[gate]?.demon || 'Belial';

  return {
    rawInput: input,
    sanitized,
    hebrew: {
      value: hebrewStandard,
      gadol: hebrewGadol,
      reduction: theosophicReduction(hebrewStandard),
      breakdown: hebrewBreakdown
    },
    greek: {
      value: greekSum,
      reduction: theosophicReduction(greekSum),
      breakdown: greekBreakdown
    },
    agrippa: {
      value: agrippaSum,
      reduction: theosophicReduction(agrippaSum),
      breakdown: agrippaBreakdown
    },
    englishALW: {
      value: englishSum,
      reduction: theosophicReduction(englishSum),
      breakdown: englishBreakdown
    },
    dukante: {
      value: dukanteSum,
      reduction: theosophicReduction(dukanteSum),
      primaryDemon,
      gate,
      breakdown: dukanteBreakdown
    }
  };
}
